// crewbit-admin/src/hooks/useScheduleUrlState.ts
//
// Schedule Redesign v1 — URL state migration to zoom/lens.
//
// Canonical URL state with legacy bookmark compatibility:
// - Reads zoom/lens/date params.
// - Falls back to legacy ?view=... when zoom is absent.
// - Writes zoom/lens/date and clears legacy view.
//
// Admin uses react-router-dom v7 useSearchParams; setter merges with existing
// params to preserve other URL state on each mutation.

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type ScheduleZoom = 'today' | 'day' | 'week' | 'month' | 'year';
export type ScheduleLens = 'calendar' | 'crew' | 'route';

export const SCHEDULE_ZOOMS_ADMIN: ReadonlyArray<ScheduleZoom> = [
  'today',
  'day',
  'week',
  'month',
  'year',
];

function todayLocal(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export interface UseScheduleUrlStateOptions {
  fallbackZoom?: ScheduleZoom;
  fallbackLens?: ScheduleLens;
}

export interface ScheduleUrlState {
  zoom: ScheduleZoom;
  lens: ScheduleLens;
  date: string;
  employee: string;
  resource: string;
  setZoom: (z: ScheduleZoom) => void;
  setLens: (l: ScheduleLens) => void;
  setDate: (d: string) => void;
  setEmployee: (e: string) => void;
  setResource: (r: string) => void;
}

export function useScheduleUrlState(opts?: UseScheduleUrlStateOptions): ScheduleUrlState {
  const [searchParams, setSearchParams] = useSearchParams();
  const fallbackZoom = opts?.fallbackZoom ?? 'day';
  const fallbackLens = opts?.fallbackLens ?? 'calendar';

  const { zoom, lens } = useMemo((): { zoom: ScheduleZoom; lens: ScheduleLens } => {
    const z = searchParams.get('zoom');
    if (z && SCHEDULE_ZOOMS_ADMIN.includes(z as ScheduleZoom)) {
      const l = searchParams.get('lens');
      if (l === 'calendar' || l === 'crew' || l === 'route') {
        return { zoom: z as ScheduleZoom, lens: l };
      }
      return { zoom: z as ScheduleZoom, lens: fallbackLens };
    }

    const legacyView = searchParams.get('view');
    switch (legacyView) {
      case 'today':
        return { zoom: 'today', lens: 'calendar' };
      case 'day':
        return { zoom: 'day', lens: 'calendar' };
      case 'week':
        return { zoom: 'week', lens: 'calendar' };
      case 'month':
        return { zoom: 'month', lens: 'calendar' };
      case 'route':
        return { zoom: 'day', lens: 'route' };
      case 'team':
        return { zoom: 'day', lens: 'crew' };
      default:
        return { zoom: fallbackZoom, lens: fallbackLens };
    }
  }, [searchParams, fallbackZoom, fallbackLens]);

  const date = useMemo(() => {
    const raw = searchParams.get('date');
    return raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : todayLocal();
  }, [searchParams]);

  const employee = searchParams.get('employee') ?? 'all';
  const resource = searchParams.get('resource') ?? 'all';

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setZoom = useCallback((z: ScheduleZoom) => {
    if (!SCHEDULE_ZOOMS_ADMIN.includes(z)) return;
    updateParams({ zoom: z, view: null });
  }, [updateParams]);
  const setLens = useCallback((l: ScheduleLens) => {
    updateParams({ lens: l, view: null });
  }, [updateParams]);
  const setDate = useCallback((d: string) => updateParams({ date: d }), [updateParams]);
  const setEmployee = useCallback((e: string) => updateParams({ employee: e }), [updateParams]);
  const setResource = useCallback((r: string) => updateParams({ resource: r }), [updateParams]);

  return { zoom, lens, date, employee, resource, setZoom, setLens, setDate, setEmployee, setResource };
}
